import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { UserPlus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

export const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }

    setLoading(true);

    try {
      const { data } = await api.post('/api/v1/auth/register', {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: 'ASEGURADO'
      });

      sessionStorage.setItem('access_token', data.access_token);
      toast.success('Cuenta creada exitosamente');
      navigate('/dashboard');
      window.location.reload(); // Reload to update auth context
    } catch (error) {
      console.error('Registration error:', error);
      
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('already registered')) {
        toast.error('Este correo ya está registrado. Intenta iniciar sesión.');
      } else {
        toast.error(error.response?.data?.detail || 'Error al crear cuenta. Intenta nuevamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[hsl(218,100%,14%)]">
      {/* Top brand strip */}
      <div className="flex flex-col items-center justify-center py-6 sm:py-10 gap-3">
        <img
          src="/logo-blanco.png"
          alt="Seguros Horizonte, S.A."
          className="h-16 sm:h-24 w-auto object-contain"
        />
        <p
          className="text-xs sm:text-sm tracking-[0.08em] text-white/85 font-semibold italic"
          style={{ fontFamily: "'Libre Baskerville', Georgia, serif" }}
          data-testid="register-brand-slogan"
        >
          Tu Bienestar es Nuestro Horizonte
        </p>
      </div>

      {/* Form area */}
      <div className="flex-1 flex items-start sm:items-center justify-center px-4 pb-8">
        <div className="w-full max-w-sm sm:max-w-md animate-fade-in">
          <Card className="shadow-2xl border-0">
            <CardHeader className="space-y-1 text-center pb-4 px-5 sm:px-6">
              <CardTitle className="text-xl sm:text-2xl gradient-text">
                Crear Cuenta
              </CardTitle>
              <CardDescription className="text-sm">
                Registrate para gestionar tus seguros
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 px-5 sm:px-6">
              <form onSubmit={handleRegister} className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="name" className="text-xs font-medium">
                    Nombre Completo
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Juan Perez"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    disabled={loading}
                    className="h-10"
                    data-testid="register-name-input"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-xs font-medium">
                    Correo Electronico
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="tu@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    autoComplete="email"
                    disabled={loading}
                    className="h-10"
                    data-testid="register-email-input"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="password" className="text-xs font-medium">
                    Contrasena
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Minimo 8 caracteres"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    autoComplete="new-password"
                    disabled={loading}
                    minLength={8}
                    className="h-10"
                    data-testid="register-password-input"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="confirm-password" className="text-xs font-medium">
                    Confirmar Contrasena
                  </Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="Repite tu contrasena"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    required
                    autoComplete="new-password"
                    disabled={loading}
                    className="h-10"
                    data-testid="register-confirm-password-input"
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="register-submit-button"
                  className="w-full h-10 font-medium"
                  disabled={loading}
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Creando cuenta...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <UserPlus className="w-4 h-4" />
                      Crear Cuenta
                    </span>
                  )}
                </Button>
              </form>

              <p className="text-center text-[10px] text-muted-foreground">
                Al registrarte, aceptas nuestros{' '}
                <a href="#" className="text-primary hover:underline font-medium">
                  Terminos y Condiciones
                </a>
              </p>
            </CardContent>
          </Card>

          <p className="text-center text-xs text-white/70 mt-4">
            Ya tienes cuenta?{' '}
            <Link to="/login" className="text-white font-medium hover:underline">
              Iniciar Sesion
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};
